import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BaseService } from 'src/app/helpers/base.service';
import { environment } from 'src/environments/environment.development';
import { hr360Menu } from '../menu/hr360.menu';
import { iTeamMenu } from '../menu/i-team.menu';
import { leadKonnektMenu } from '../menu/lead-konnekt.menu';
import { setupMenu } from '../menu/setup.menu';
import { recruitmentMenu } from '../menu/recruitment.menu';
import { tasksMenu } from '../menu/tasks.menu';
import { projectsMenu } from '../menu/projects.menu';
import { qaMenu } from '../menu/qa.menu';
import { PMMenu } from '../menu/pm.menu';
import { iNestMenu } from '../menu/i-nest.menu';
import { crmMenu } from '../menu/crm.menu';
import { leavesMenu } from '../menu/leaves.menu';
import { organizationMenu } from '../menu/organization.menu';
import { attendanceMenu } from '../menu/attendance.menu';
import { movementDiaryMenu } from '../menu/movement-diary.menu';
import { CoreMenu } from 'src/@core/types';

export interface EmployeeRedirectSetting {
  id?: string | null;
  defaultRedirectCode: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmployeeRedirectSettingService extends BaseService {
  private readonly _endpoint = `${environment.base_url}/api/employeeredirectsetting`;

  constructor() {
    super();
  }

  getByEmployee(): Observable<EmployeeRedirectSetting | null> {
    return this._http
      .get<any>(`${this._endpoint}/getbyemployee`, { responseType: 'json' })
      .pipe(
        map((res) => {
          if (!res) return null;
          const data = res?.data ?? res;
          if (!data || (!data.defaultRedirectCode && !data.id)) return null;
          return {
            id: data.id ?? null,
            defaultRedirectCode: data.defaultRedirectCode ?? '',
          };
        })
      );
  }

  add(payload: EmployeeRedirectSetting): Observable<any> {
    const body = {
      defaultRedirectCode: payload.defaultRedirectCode,
    };
    return this._http.post<any>(`${this._endpoint}/add`, body, {
      responseType: 'json',
    });
  }

  update(payload: EmployeeRedirectSetting): Observable<any> {
    const body = {
      id: payload.id,
      defaultRedirectCode: payload.defaultRedirectCode,
    };
    return this._http.put<any>(`${this._endpoint}/update`, body, {
      responseType: 'json',
    });
  }

 
  resolveRouteByCode(code: string): string | null {
    if (!code) return null;
    const cleanCode = code.trim().toUpperCase();
    const staticMap: { [key: string]: string } = {
      // Lead Konnekt
      I1011: `${environment.leadKonnekt_url}/app/all-leads`,
      I1000: `${environment.leadKonnekt_url}/app/all-forms`,
      I1016: '/lead-konnekt/tickets',
      I1007: '/lead-konnekt/organizations',
      I: '/lead-konnekt',

      // HR360
      F1004: '/hr360/attendance/self',
      F1005: '/hr360/attendance/all',
      F1007: '/hr360/leave-management',
      F1008: '/hr360/claims',
      F1010: '/hr360/employees',
      F1111: '/hr360/recruitment',
      F1066: '/app/payroll',
      F1060: '/hr360/reports/person',
      F5442: '/hr360/calendar',
      // F1439: `${environment.fnf_url}`,
      F: '/hr360',

      // Root
      R: '/dashboard',

      // I-Team
      H1008: '/i-team/tasks',
      H1025: '/i-team/tasks/board',
      H1015: '/i-team/projects',
      H1038: '/i-team/qa/test-scenarios',
      H: '/i-team',

      // Setup
      L1081: '/setup/workspace',
      L1008: '/setup/company',
      L1070: '/setup/station',
      L1016: '/setup/department',
      L1020: '/setup/designation',
      L1000: '/setup/roles',
      L1060: '/setup/shifts',
      L1066: '/setup/qualification',
      L: '/setup',

      // I-Nest
      N1001: '/i-nest/attendance/self',
      N: '/i-nest',

      // Support
      M: '/app/support',

      // Meetings
      J: '/app/meetings',

      // Chat
      B: '/app/chat',

      // Forms
      E: '/app/easy-form',

      // Invoicing
      P: '/invoicing',

      // iSchool
      G: `${environment.ischool_url}`,

      // Asset Pro
      A: `${environment.inventory_url}`,

      // Easy Accounts
      C: `${environment.accounting_url}`,

      // Easy Fee
      D: `${environment.easy_fee_url}`,

      // SQA
      O: `${environment.sqa_url}`,

      // Movement Diary
      Q: '/movement-diary',

      // My Profile
      K: '/app/profile',
    };

    if (staticMap[cleanCode]) {
      return staticMap[cleanCode];
    }

    // 2. Scan core menu arrays
    const allMenus: CoreMenu[][] = [
      hr360Menu,
      iTeamMenu,
      leadKonnektMenu,
      setupMenu,
      recruitmentMenu,
      tasksMenu,
      projectsMenu,
      qaMenu,
      PMMenu,
      iNestMenu,
      crmMenu,
      leavesMenu,
      organizationMenu,
      attendanceMenu,
      movementDiaryMenu,
    ];

    for (const menuList of allMenus) {
      const found = this._findUrlInMenu(menuList, cleanCode);
      if (found) return found;
    }

    return null;
  }

  private _findUrlInMenu(menuList: CoreMenu[], code: string): string | null {
    if (!menuList || !menuList.length) return null;

    for (const item of menuList) {
      const right = item.role?.right;
      let matches = false;
      if (typeof right === 'string' && right.toUpperCase() === code) {
        matches = true;
      } else if (Array.isArray(right) && right.some((r) => r.toUpperCase() === code)) {
        matches = true;
      }

      if (matches && item.url) {
        return item.url;
      }

      if (item.children && item.children.length) {
        const childMatch = this._findUrlInMenu(item.children, code);
        if (childMatch) return childMatch;
      }
    }
    return null;
  }
}
